import {
  type DataPerspective,
  TANK_READINGS_PAGE_SIZE,
  type TankReadingsFilters,
  type TankReadingsOverviewRow,
  fetchTankReadings,
} from '@/api/hooks/useTankReadings'
import { useServerSideDataSource } from '@/api/useServerSideDatasource/useServerSideDatasource'
import { useDebouncedSearch } from '@/pages/Dashboard/panels/ReferenceDataPanel/hooks/useDebouncedSearch'
import { GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { GetRowIdParams } from 'ag-grid-community'
import { Input, Select } from 'antd'
import { useMemo, useState } from 'react'
import { tankReadingsColumnDefs } from './tankReadingsColumnDefs'

// Stable ref: the only true server-side filter is the optional partner, which the
// page doesn't set yet. A constant keeps the SSRM datasource from recreating each
// render (which would reset the grid to startRow=0). The Data Format perspective
// is NOT here — it's a client-side display choice (rows carry all three label-sets).
const EMPTY_FILTERS: TankReadingsFilters = {}

export function TankReadingsPage() {
  const [perspective, setPerspective] = useState<DataPerspective>('internal')

  // Debounced search drives the backend (it matches across all three perspectives);
  // changing it recreates the datasource, which refetches from the first page.
  const { searchTerm, setSearchTerm } = useDebouncedSearch()

  const { serverSideDatasource, isFetching, rowCount } =
    useServerSideDataSource<TankReadingsOverviewRow, TankReadingsFilters>(
      EMPTY_FILTERS,
      searchTerm,
      fetchTankReadings,
    )

  // Rebuilding column defs on perspective change refreshes the identity/location
  // cells from the already-loaded page rows — no refetch (perspective-dependent
  // columns are intentionally not server-sortable).
  const columnDefs = useMemo(
    () => tankReadingsColumnDefs(perspective),
    [perspective],
  )

  const agPropOverrides = useMemo(
    () => ({
      rowModelType: 'serverSide' as const,
      serverSideDatasource,
      pagination: true,
      paginationPageSize: TANK_READINGS_PAGE_SIZE,
      cacheBlockSize: TANK_READINGS_PAGE_SIZE,
      getRowId: (p: GetRowIdParams<TankReadingsOverviewRow>) =>
        `${p.data.partner_tenant_id}:${p.data.tank_goid}`,
      defaultColDef: {
        sortable: false,
        // Perspective-dependent + date columns aren't server-filterable; only the
        // columns that opt in (Partner/Volume/Status) get a filter. Search covers
        // the rest across all perspectives.
        filter: false,
        resizable: true,
      },
      headerHeight: 40,
    }),
    [serverSideDatasource],
  )

  return (
    <Vertical className='h-full p-3'>
      <Vertical flex='1' style={{ minHeight: 0 }}>
        <GraviGrid
          columnDefs={columnDefs}
          agPropOverrides={agPropOverrides}
          loading={isFetching}
          controlBarProps={{
            // excalibrr's ControlBarProps type omits rowCount, but GraviGrid reads
            // it at runtime for the result count (the SSRM datasource has no
            // rowData prop to count). Matches the MasterData grid usage.
            // @ts-expect-error -- rowCount is a real runtime prop missing from the type
            rowCount,
            title: 'Tank Readings',
            customSearchBar: (
              <Input
                placeholder='Search tanks, sites, partners'
                allowClear
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            ),
            actionButtons: (
              <Horizontal alignItems='center' gap={6}>
                <Texto appearance='hint' category='label'>
                  Data Format:
                </Texto>
                <Select
                  value={perspective}
                  onChange={setPerspective}
                  style={{ width: 120 }}
                >
                  <Select.Option value='internal'>Internal</Select.Option>
                  <Select.Option value='gravitate'>Gravitate</Select.Option>
                  <Select.Option value='partner'>Partner</Select.Option>
                </Select>
              </Horizontal>
            ),
          }}
          storageKey='tank-readings-grid'
        />
      </Vertical>
    </Vertical>
  )
}
